<script setup lang="ts" generic="T extends string | number | boolean | object">
/*
 * Accessible dropdown built on @headlessui/vue Listbox. Follows the same
 * border/radius/focus tokens as TextInput so they read as a set in forms.
 */
import { computed, inject, type Ref, type ComputedRef } from 'vue';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { Check, ChevronDown } from 'lucide-vue-next';

interface Option<V> {
  label: string;
  value: V;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue: T | null;
    options: Option<T>[];
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    size?: 'sm' | 'md';
    ariaLabel?: string;
    /**
     * Panel sizing. Default (`true`) keeps the historical behaviour — the option
     * panel grows to fit the longest option (`min-w-full w-max`) and labels wrap.
     * Pass `false` to clamp the panel + trigger to the control width and truncate
     * long labels with a hover tooltip (used in narrow modals so long options
     * can't clip horizontally or push the trigger onto two lines). Display only —
     * the emitted value is unaffected.
     */
    fitContent?: boolean;
  }>(),
  { placeholder: 'Select…', size: 'md', fitContent: true },
);

defineEmits<{ 'update:modelValue': [v: T | null] }>();

const field = inject<{
  inputId: Ref<string>;
  describedBy: ComputedRef<string>;
  invalid: ComputedRef<boolean>;
}>('formField', undefined as never);

const isInvalid = computed(() => props.invalid || field?.invalid.value || false);
const selectedLabel = computed(
  () => props.options.find((o) => o.value === props.modelValue)?.label ?? '',
);
</script>

<template>
  <Listbox
    :model-value="modelValue"
    :disabled="disabled"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="relative">
      <ListboxButton
        :id="field?.inputId.value"
        :aria-label="ariaLabel || undefined"
        :aria-invalid="isInvalid || undefined"
        :aria-describedby="field?.describedBy.value || undefined"
        :class="[
          'flex w-full items-center justify-between gap-2 border bg-app-surface text-left text-app-fg transition-shadow',
          'focus:outline-none focus:ring-3 focus:ring-primary/20 focus:border-primary',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
          // sm gets a tighter radius + smaller text to match the reduced height.
          size === 'sm' ? 'rounded-md px-3 py-1.5 text-sm' : 'rounded-xl px-3.5 py-2.5 text-base',
          isInvalid ? 'border-red-300' : 'border-app-border',
        ]"
      >
        <!-- When clamped (fitContent=false) the selected label truncates to a single
             line with a hover tooltip; otherwise it renders as before. Display only. -->
        <span
          :title="!fitContent && modelValue != null ? selectedLabel : undefined"
          :class="[
            !fitContent ? 'min-w-0 flex-1 truncate' : '',
            modelValue == null ? 'text-app-muted' : '',
          ]"
        >
          {{ modelValue == null ? placeholder : selectedLabel }}
        </span>
        <ChevronDown class="h-4 w-4 shrink-0 text-app-muted" aria-hidden="true" />
      </ListboxButton>
      <transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          :class="[
            'absolute z-30 mt-1 max-h-60 overflow-auto border border-app-border bg-app-surface py-1 shadow-popup focus:outline-none',
            // Panel radius tracks the trigger: tighter for sm.
            size === 'sm' ? 'rounded-md' : 'rounded-xl',
            fitContent ? 'min-w-full w-max' : 'w-full',
          ]"
        >
          <ListboxOption
            v-for="opt in options"
            :key="String(opt.value)"
            v-slot="{ active, selected }"
            :value="opt.value"
            :disabled="opt.disabled"
            as="template"
          >
            <li
              :class="[
                'flex cursor-pointer items-center gap-2 px-3',
                // Option text/padding track the trigger: smaller + denser for sm.
                size === 'sm' ? 'py-1.5 text-sm' : 'py-2 text-md',
                active ? 'bg-primary-50 text-primary-700' : 'text-app-fg',
                opt.disabled ? 'cursor-not-allowed opacity-50' : '',
              ]"
            >
              <Check
                class="h-4 w-4 shrink-0"
                :class="selected ? 'text-primary' : 'opacity-0'"
                aria-hidden="true"
              />
              <!-- When clamped (fitContent=false) options truncate with a hover
                   tooltip so they never clip inside a narrow modal; otherwise the
                   panel grew to fit and labels stay on one line. -->
              <span
                :title="!fitContent ? opt.label : undefined"
                :class="[
                  !fitContent ? 'min-w-0 flex-1 truncate' : 'whitespace-nowrap',
                  selected ? 'font-medium' : '',
                ]"
                >{{ opt.label }}</span
              >
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>
